<script setup>
import { formatDate, formatNumber, formatRupiah } from '@/composables/useFormat'
import { useApi } from '@/utils/api'; // ← GANTI dari $api ke useApi

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Report',
  },
})

// ── State ─────────────────────────────────────────────────────────────────────
const loading  = ref(false)
const dateFrom = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10))
const dateTo   = ref(new Date().toISOString().slice(0, 10))
const report   = ref(null)

// ── Fetch ─────────────────────────────────────────────────────────────────────
const fetchReport = async () => {
  if (!dateFrom.value || !dateTo.value) return
  loading.value = true
  try {
    // ✅ FIX: Pakai useApi + URL tanpa prefix /api
    const response = await useApi('/v1/reports/profit-loss', {
      query: { date_from: dateFrom.value, date_to: dateTo.value },
    }).get()
    
    report.value = response?.data ?? response
  } catch (err) {
    console.error('❌ Profit loss error:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchReport)

// ── Computed ──────────────────────────────────────────────────────────────────
const isProfit = computed(() => (report.value?.net_profit ?? 0) >= 0)

const plRows = computed(() => {
  if (!report.value) return []
  const r = report.value
  return [
    { label: 'Revenue (Omzet)', value: r.revenue, type: 'income', bold: false },
    { label: 'Total Diskon', value: -r.total_discount, type: 'deduct', bold: false },
    { label: 'HPP (Harga Pokok)', value: -r.hpp, type: 'deduct', bold: false },
    { label: 'Laba Kotor', value: r.gross_profit, type: 'subtotal', bold: true, margin: `${r.gross_margin}%` },
  ]
})

const expenseRows = computed(() => report.value?.expenses ?? [])

const exportFile = (format) => {
  const params = new URLSearchParams({
    date_from: dateFrom.value,
    date_to:   dateTo.value,
  })
  window.open(`/api/v1/export/profit-loss/${format}?${params}`, '_blank')
}
</script>

<template>
  <div>
    <!-- Page Header -->
    <VRow class="mb-4 align-center">
      <VCol cols="12" md="6">
        <h4 class="text-h4 font-weight-medium mb-1">Laporan Laba Rugi</h4>
        <p class="text-body-2 text-medium-emphasis">Profit & Loss Statement</p>
      </VCol>
    </VRow>

    <!-- Filter -->
    <VCard class="mb-4">
      <VCardText>
        <VRow align="center">
          <VCol cols="12" sm="3">
            <VTextField
              v-model="dateFrom"
              type="date"
              label="Dari Tanggal"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol cols="12" sm="3">
            <VTextField
              v-model="dateTo"
              type="date"
              label="Sampai Tanggal"
              density="compact"
              hide-details
            />
          </VCol>
          <VCol cols="12" sm="2">
            <VBtn
              color="primary"
              block
              :loading="loading"
              prepend-icon="ri-search-line"
              @click="fetchReport"
            >
              Tampilkan
            </VBtn>
          </VCol>
          <VCol cols="12" sm="4" class="d-flex justify-end gap-2">
            <VBtn
              prepend-icon="ri-file-excel-line"
              color="success"
              variant="outlined"
              size="small"
              @click="exportFile('excel')"
            >
              Excel
            </VBtn>
            <VBtn
              prepend-icon="ri-file-pdf-line"
              color="error"
              variant="outlined"
              size="small"
              @click="exportFile('pdf')"
            >
              PDF
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Empty -->
    <VCard v-if="!loading && !report" class="mb-4">
      <VCardText class="text-center py-12">
        <VIcon icon="ri-file-chart-line" size="64" color="secondary" class="mb-3" />
        <p class="text-h6 font-weight-medium mb-1">Pilih rentang tanggal</p>
        <p class="text-body-2 text-medium-emphasis">dan klik Tampilkan untuk melihat laporan</p>
      </VCardText>
    </VCard>

    <template v-else-if="report">
      <!-- Net Profit Banner -->
      <VAlert
        :type="isProfit ? 'success' : 'error'"
        variant="tonal"
        class="mb-4"
        :icon="isProfit ? 'ri-trophy-line' : 'ri-alert-line'"
      >
        <div class="d-flex align-center justify-space-between flex-wrap gap-2">
          <div>
            <div class="text-body-1 font-weight-bold">
              {{ isProfit ? 'Laba Bersih' : 'Rugi Bersih' }} Periode
              {{ formatDate(report.date_from) }} – {{ formatDate(report.date_to) }}
            </div>
            <div class="text-caption">
              {{ report.total_orders }} transaksi · Net margin {{ report.net_margin }}%
            </div>
          </div>
          <div class="text-h5 font-weight-bold">
            {{ formatRupiah(report.net_profit) }}
          </div>
        </div>
      </VAlert>

      <VRow>
        <!-- P&L Statement -->
        <VCol cols="12" md="6">
          <VCard :loading="loading">
            <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
              Income Statement
            </VCardTitle>
            <VDivider />
            <VList density="compact">

              <!-- Revenue section -->
              <VListSubheader>PENDAPATAN</VListSubheader>
              <VListItem>
                <VListItemTitle class="text-body-2">Revenue (Omzet)</VListItemTitle>
                <template #append>
                  <span class="text-body-2 font-weight-medium text-success">
                    {{ formatRupiah(report.revenue) }}
                  </span>
                </template>
              </VListItem>
              <VListItem>
                <VListItemTitle class="text-body-2 ps-4">Diskon diberikan</VListItemTitle>
                <template #append>
                  <span class="text-body-2 text-warning">
                    ({{ formatRupiah(report.total_discount) }})
                  </span>
                </template>
              </VListItem>
              <VListItem>
                <VListItemTitle class="text-body-2 ps-4">Pajak dipungut</VListItemTitle>
                <template #append>
                  <span class="text-body-2 text-medium-emphasis">
                    {{ formatRupiah(report.total_tax) }}
                  </span>
                </template>
              </VListItem>

              <VDivider class="my-1" />

              <!-- COGS -->
              <VListSubheader>HARGA POKOK PENJUALAN</VListSubheader>
              <VListItem>
                <VListItemTitle class="text-body-2">HPP (dari resep)</VListItemTitle>
                <template #append>
                  <span class="text-body-2 text-error">
                    ({{ formatRupiah(report.hpp) }})
                  </span>
                </template>
              </VListItem>

              <VDivider class="my-1" />

              <!-- Gross Profit -->
              <VListItem class="bg-info-subtle">
                <VListItemTitle class="text-body-2 font-weight-bold">LABA KOTOR</VListItemTitle>
                <template #append>
                  <div class="text-end">
                    <div class="text-body-2 font-weight-bold text-info">
                      {{ formatRupiah(report.gross_profit) }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      Margin {{ report.gross_margin }}%
                    </div>
                  </div>
                </template>
              </VListItem>

              <VDivider class="my-1" />

              <!-- Expenses -->
              <VListSubheader>PENGELUARAN OPERASIONAL</VListSubheader>
              <VListItem
                v-for="exp in expenseRows"
                :key="exp.category_id"
              >
                <template #prepend>
                  <div
                    class="rounded-circle mr-2"
                    :style="`width:10px;height:10px;background:${exp.category_color ?? '#9e9e9e'}`"
                  />
                </template>
                <VListItemTitle class="text-body-2">{{ exp.category_name }}</VListItemTitle>
                <template #append>
                  <span class="text-body-2 text-error">
                    ({{ formatRupiah(exp.total) }})
                  </span>
                </template>
              </VListItem>
              <VListItem>
                <VListItemTitle class="text-body-2 font-weight-semibold">Total Pengeluaran</VListItemTitle>
                <template #append>
                  <span class="text-body-2 font-weight-semibold text-error">
                    ({{ formatRupiah(report.total_expense) }})
                  </span>
                </template>
              </VListItem>

              <VDivider class="my-1" />

              <!--
                Modul AV (Addendum 11 §5): refund selalu tunai, mengurangi
                laba bersih terlepas dari metode bayar order aslinya --
                baris terpisah dari Diskon (yang sudah otomatis "net" di
                dalam Revenue di atas, ini murni kas keluar tambahan).
              -->
              <VListSubheader>REFUND</VListSubheader>
              <VListItem>
                <VListItemTitle class="text-body-2 font-weight-semibold">Total Refund</VListItemTitle>
                <template #append>
                  <span class="text-body-2 font-weight-semibold text-error">
                    ({{ formatRupiah(report.total_refund) }})
                  </span>
                </template>
              </VListItem>

              <VDivider class="my-1" />

              <!-- Net Profit -->
              <VListItem :class="isProfit ? 'bg-success-subtle' : 'bg-error-subtle'">
                <VListItemTitle class="text-body-2 font-weight-bold">
                  {{ isProfit ? 'LABA BERSIH' : 'RUGI BERSIH' }}
                </VListItemTitle>
                <template #append>
                  <div class="text-end">
                    <div
                      class="text-body-1 font-weight-bold"
                      :class="isProfit ? 'text-success' : 'text-error'"
                    >
                      {{ formatRupiah(report.net_profit) }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      Net margin {{ report.net_margin }}%
                    </div>
                  </div>
                </template>
              </VListItem>

            </VList>
          </VCard>
        </VCol>

        <!-- Expense Breakdown Chart (visual list) -->
        <VCol cols="12" md="6">
          <VCard :loading="loading">
            <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
              Breakdown Pengeluaran
            </VCardTitle>
            <VDivider />

            <VCardText v-if="expenseRows.length === 0" class="text-center py-8">
              <VIcon icon="ri-money-dollar-circle-line" size="48" color="secondary" class="mb-2" />
              <p class="text-body-2 text-medium-emphasis">Tidak ada pengeluaran pada periode ini</p>
            </VCardText>

            <VCardText v-else class="pa-4">
              <div
                v-for="exp in expenseRows"
                :key="exp.category_id"
                class="mb-4"
              >
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="d-flex align-center gap-2">
                    <div
                      class="rounded-circle"
                      :style="`width:12px;height:12px;background:${exp.category_color ?? '#9e9e9e'}`"
                    />
                    <span class="text-body-2">{{ exp.category_name }}</span>
                  </div>
                  <span class="text-body-2 font-weight-medium">
                    {{ formatRupiah(exp.total) }}
                  </span>
                </div>
                <VProgressLinear
                  :model-value="report.total_expense > 0 ? (exp.total / report.total_expense) * 100 : 0"
                  :color="exp.category_color ?? 'primary'"
                  height="6"
                  rounded
                />
                <div class="text-caption text-medium-emphasis text-end">
                  {{ report.total_expense > 0 ? ((exp.total / report.total_expense) * 100).toFixed(1) : 0 }}%
                  dari total pengeluaran
                </div>
              </div>
            </VCardText>
          </VCard>

          <!-- Quick Stats -->
          <VCard class="mt-4" :loading="loading">
            <VCardTitle class="text-body-1 font-weight-semibold pa-4 pb-2">
              Ringkasan
            </VCardTitle>
            <VDivider />
            <VList density="compact">
              <VListItem>
                <VListItemTitle class="text-body-2">Total Transaksi</VListItemTitle>
                <template #append>
                  <span class="text-body-2 font-weight-medium">{{ formatNumber(report.total_orders) }}</span>
                </template>
              </VListItem>
              <VListItem>
                <VListItemTitle class="text-body-2">Rata-rata per Transaksi</VListItemTitle>
                <template #append>
                  <span class="text-body-2 font-weight-medium">
                    {{ report.total_orders > 0 ? formatRupiah(report.revenue / report.total_orders) : '-' }}
                  </span>
                </template>
              </VListItem>
              <VListItem>
                <VListItemTitle class="text-body-2">Gross Margin</VListItemTitle>
                <template #append>
                  <span class="text-body-2 font-weight-medium text-info">{{ report.gross_margin }}%</span>
                </template>
              </VListItem>
              <VListItem>
                <VListItemTitle class="text-body-2">Net Margin</VListItemTitle>
                <template #append>
                  <span
                    class="text-body-2 font-weight-bold"
                    :class="isProfit ? 'text-success' : 'text-error'"
                  >
                    {{ report.net_margin }}%
                  </span>
                </template>
              </VListItem>
            </VList>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>
